import * as React from 'react'

import { SubNavigationBar } from '@/modules/shared/components'
import { CompanyNameBadge } from '@/modules/drivers/shared/viewBars/CompanyNameBadge'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TableProvider } from '@/modules/drivers/context/TableContext'
import { DatabaseBackupUpload } from '../components/DatabaseBackupUpload'
import CompanyDatabasesPanel from '../components/CompanyDatabasesPanel'
import DuplicateDatabaseModal from '../components/DuplicateDatabaseModal'
import AddDatabaseModal from '../components/AddDatabaseModal'
import { RouteComponentProps } from '@/app/navigation/withRouter'

interface DatabasesPageRouteParams {
  companyId: string
}

type TabOption = 'databases' | 'backup'

interface TabButtonProps {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, children }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`tw-z-10 tw-text-nowrap tw-p-4 tw-text-sm tw-font-medium tw-transition-colors hover:tw-no-underline ${
        active
          ? 'tw-z-10 tw-border-b-[2px] tw-border-blue-600 tw-font-medium tw-text-blue-600'
          : 'tw-border-b-[2px] tw-border-transparent tw-text-content-base hover:tw-text-blue-600'
      }`}
    >
      {children}
    </button>
  )
}

export const DatabasesPage: React.FC<RouteComponentProps<DatabasesPageRouteParams>> = ({
  match,
}) => {
  const { companyId } = match.params
  const { t } = useLocalize()
  const [activeTab, setActiveTab] = React.useState<TabOption>('databases')
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = React.useState(false)
  const [isAddDatabaseModalOpen, setIsAddDatabaseModalOpen] = React.useState(false)

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <div className="tw-flex tw-items-center tw-border-b tw-p-4">
        <CompanyNameBadge />
      </div>
      <SubNavigationBar>
        <TabButton
          active={activeTab === 'databases'}
          onClick={() => setActiveTab('databases')}
        >
          {t('databases.databases-tab')}
        </TabButton>
        <TabButton
          active={activeTab === 'backup'}
          onClick={() => setActiveTab('backup')}
        >
          {t('databases.from-backup-tab')}
        </TabButton>
      </SubNavigationBar>

      {activeTab === 'databases' && (
        <TableProvider bulk={true}>
          <CompanyDatabasesPanel
            companyId={companyId}
            onDuplicate={() => setIsDuplicateModalOpen(true)}
            onAddDatabase={() => setIsAddDatabaseModalOpen(true)}
          />
        </TableProvider>
      )}
      {activeTab === 'backup' && (
        <div className="tw-p-4">
          <DatabaseBackupUpload companyId={companyId} />
        </div>
      )}

      <DuplicateDatabaseModal
        isOpen={isDuplicateModalOpen}
        close={() => setIsDuplicateModalOpen(false)}
        selectedCompanyIds={[companyId]}
        companyId={companyId}
      />
      <AddDatabaseModal
        isOpen={isAddDatabaseModalOpen}
        close={() => setIsAddDatabaseModalOpen(false)}
        companyId={companyId}
      />
    </div>
  )
}
